import { useEffect, useRef } from 'react';

/**
 * The slim icon rail that replaces the stacked sidebar while the scratchpad
 * is open.
 *
 * The panels themselves are handed in already built by the page, so the rail
 * and the stacked sidebar render the exact same components with the exact
 * same props. Nothing about a panel changes because it is in a drawer.
 */
export interface RailPanel {
  key: string;
  /** Tooltip and accessible name for the icon. */
  label: string;
  icon: React.ReactNode;
  /** Count shown on the icon, e.g. open comment threads. Omit or 0 to hide. */
  badge?: number;
  /** Draw a hairline above this icon, grouping the rail. */
  divider?: boolean;
  panel: React.ReactNode;
}

/**
 * The drawer sits flush against the rail's left edge. The rail is the last
 * 3.5rem of the editor's centred max-w-7xl (80rem) container, which has 2rem
 * of side padding at md and up, so its left edge from the viewport's right is
 * the centring margin plus 5.5rem.
 */
const DRAWER_RIGHT = 'calc(max(0px, (100vw - 80rem) / 2) + 5.5rem)';

function railButtonId(key: string) {
  return `script-panel-${key}`;
}

export default function SidebarRail({
  panels,
  openKey,
  onToggle,
  className = '',
}: {
  panels: RailPanel[];
  openKey: string | null;
  onToggle: (key: string) => void;
  className?: string;
}) {
  return (
    <nav
      aria-label="Script panels"
      data-print-hide
      className={`relative z-[60] flex flex-col items-center gap-1 py-4 border-l border-line bg-elevated ${className}`}
    >
      {panels.map((p) => (
        <div key={p.key} className="contents">
          {p.divider && <span className="w-4 h-px bg-line my-1.5" aria-hidden />}
          <button
            id={railButtonId(p.key)}
            type="button"
            onClick={() => onToggle(p.key)}
            title={p.label}
            aria-label={p.label}
            aria-expanded={openKey === p.key}
            className={`
              relative w-9 h-9 rounded-sm flex items-center justify-center
              transition-colors duration-150
              ${
                openKey === p.key
                  ? 'bg-card text-accent shadow-flare/30'
                  : 'text-faint hover:bg-card hover:text-fg'
              }
            `}
          >
            {p.icon}
            {p.badge != null && p.badge > 0 && (
              <span
                className="
                  absolute top-0.5 right-0.5 min-w-[15px] h-[15px] px-[3px]
                  rounded-full bg-accent text-accent-fg
                  font-mono text-[9px] font-medium
                  flex items-center justify-center
                "
              >
                {p.badge}
              </span>
            )}
          </button>
        </div>
      ))}
    </nav>
  );
}

/** Keeps every panel mounted while presenting it as either a stacked column or drawer. */
export function SidebarPanels({
  panels,
  mode,
  openKey,
  onClose,
  stackedClassName = '',
  stackedHidden = false,
}: {
  panels: (RailPanel & { inRail: boolean })[];
  mode: 'stacked' | 'drawer' | 'mobile';
  openKey: string | null;
  onClose: () => void;
  /** Extra classes on the stacked column (spacing only; it is already a flex column). */
  stackedClassName?: string;
  /** Hide the stacked column entirely (mobile, Scratch pane selected). */
  stackedHidden?: boolean;
}) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const panelRefs = useRef(new Map<string, HTMLDivElement>());
  const lastOpenKeyRef = useRef<string | null>(null);
  const openPanel = mode === 'drawer'
    ? panels.find((panel) => panel.inRail && panel.key === openKey) ?? null
    : null;
  const openPanelKey = openPanel?.key ?? null;

  useEffect(() => {
    if (mode !== 'drawer' || !openPanelKey) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mode, onClose, openPanelKey]);

  useEffect(() => {
    let focusFrame: number | null = null;
    if (mode === 'drawer' && openPanelKey) {
      lastOpenKeyRef.current = openPanelKey;
      focusFrame = window.requestAnimationFrame(() => {
        const drawer = drawerRef.current;
        const openContainer = panelRefs.current.get(openPanelKey);
        // Leave focus alone only when it already sits inside the panel that is
        // now open (an autofocused composer, say). Safari does not focus a
        // clicked button, so "anywhere in the drawer" would strand focus in
        // the panel the user just left.
        if (openContainer?.contains(document.activeElement)
          && document.activeElement instanceof HTMLElement
          && document.activeElement.getClientRects().length > 0) return;
        const firstFocusable = Array.from(openContainer?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? []).find((element) => element.getClientRects().length > 0
          && getComputedStyle(element).visibility !== 'hidden'
          && !element.closest('[inert], [aria-hidden="true"]'));
        (firstFocusable ?? drawer)?.focus();
      });
    } else if (lastOpenKeyRef.current) {
      document.getElementById(railButtonId(lastOpenKeyRef.current))?.focus();
      lastOpenKeyRef.current = null;
    }
    return () => {
      if (focusFrame !== null) window.cancelAnimationFrame(focusFrame);
    };
  }, [mode, openPanelKey]);

  return (
    // In stacked mode this outer div is the grid item, so it stretches to the
    // row height. It is also the flex column the panels sit in directly (the
    // shell, list and per-panel wrappers below are all display: contents), so
    // the comment sidebar's md:sticky gets the full-height column as its
    // containing block, exactly as it did before the panels moved in here.
    <div
      className={
        mode === 'drawer'
          ? openPanel ? 'contents' : 'hidden'
          : mode === 'mobile' ? 'mobile-panel-page' : `flex flex-col gap-4 ${stackedClassName}`
      }
      data-print-hide
      hidden={mode === 'mobile' ? !openKey : mode === 'drawer' ? !openPanel : stackedHidden}
    >
      {mode === 'drawer' && (
        <div
          key="drawer-scrim"
          className="fixed inset-0 bg-ink-900/40 z-40 animate-fade-in"
          onClick={onClose}
          aria-hidden
        />
      )}
      {/* Always a div: swapping the element type per mode would make React
          remount every panel on each pad toggle, losing drafts. The dialog
          semantics are applied as attributes in drawer mode instead. */}
      <div
        key="panels-shell"
        ref={drawerRef}
        role={mode === 'drawer' ? 'dialog' : undefined}
        aria-modal={mode === 'drawer' ? 'true' : undefined}
        aria-label={mode === 'drawer' ? openPanel?.label : undefined}
        tabIndex={mode === 'drawer' ? -1 : undefined}
        className={mode === 'drawer'
          ? `
              fixed top-[53px] bottom-0 w-[380px] z-50
              bg-card border-l border-line shadow-flare-lg/30
              flex flex-col animate-rise-subtle
            `
          : 'contents'}
        style={mode === 'drawer' ? { right: DRAWER_RIGHT } : undefined}
      >
        {mode === 'drawer' && (
          <div
            key="drawer-header"
            className="shrink-0 flex items-center justify-between gap-3 px-5 py-3 border-b border-line"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
              {openPanel?.label}
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close panel"
              title="Close panel"
              className="w-7 h-7 rounded-sm text-faint hover:bg-elevated hover:text-fg transition-colors duration-150"
            >
              <span aria-hidden>×</span>
            </button>
          </div>
        )}
        <div
          key="panels"
          className={mode === 'drawer' ? 'flex-1 min-h-0 overflow-y-auto p-4' : 'contents'}
        >
          {panels.map((panel) => (
            <div
              key={panel.key}
              ref={(element) => {
                if (element) panelRefs.current.set(panel.key, element);
                else panelRefs.current.delete(panel.key);
              }}
              className={mode === 'stacked' ? 'contents' : undefined}
              hidden={mode === 'mobile' ? panel.key !== openKey : mode === 'drawer' && (!panel.inRail || panel.key !== openKey)}
            >
              {panel.panel}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Shared wrapper so every rail icon is the same weight and size. */
export function RailIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}
